/**
 * TimelinePage コンポーネント
 *
 * 活動年表ページ。作品とイベントを時系列で表示し、
 * ジャンル・年代範囲によるフィルタ機能を提供。
 *
 */

import { useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Header } from '@/components/organisms/Header';
import { Footer } from '@/components/organisms/Footer';
import { TimelineView } from '@/components/organisms/TimelineView';
import { useLanguageStore } from '@/stores/language-store';
import { useDataStore } from '@/stores/data-store';
import {
  mergeWorksAndEvents,
  extractGenresFromTimelineItems,
  getAvailableDecades,
} from '@/lib/timeline-data';
import {
  filterTimelineItems,
  filterChronologyEntries,
} from '@/lib/data-filters';
import { useI18n } from '@/i18n/useI18n';
import { cn } from '@/lib/cn';
import { getGenreBackgroundClass } from '@/lib/genre-colors';
import {
  buildTimelineQueryParams,
  parseTimelineQueryParams,
} from '@/lib/url-filter-helpers';

/**
 * TimelinePage コンポーネント
 *
 * 使用例:
 * ```tsx
 * <TimelinePage />
 * ```
 */
export function TimelinePage() {
  const { language } = useLanguageStore();
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();

  // data-store 経由でキャッシュ済みデータを利用
  const {
    works,
    events,
    chronology,
    worksLoaded,
    eventsLoaded,
    chronologyLoaded,
    isLoadingWorks,
    isLoadingEvents,
    isLoadingChronology,
    worksError,
    eventsError,
    chronologyError,
    fetchWorks,
    fetchEvents,
    fetchChronology,
  } = useDataStore();

  // データ読み込み（未ロードの場合のみ。Promise.all相当の並列取得）
  useEffect(() => {
    if (!worksLoaded && !worksError) fetchWorks();
    if (!eventsLoaded && !eventsError) fetchEvents();
    if (!chronologyLoaded && !chronologyError) fetchChronology();
  }, [
    worksLoaded,
    eventsLoaded,
    chronologyLoaded,
    worksError,
    eventsError,
    chronologyError,
    fetchWorks,
    fetchEvents,
    fetchChronology,
  ]);

  const isLoading = isLoadingWorks || isLoadingEvents || isLoadingChronology;
  const allLoaded = worksLoaded && eventsLoaded && chronologyLoaded;
  const error = worksError || eventsError || chronologyError;

  // 作品とイベントを統合
  const timelineItems = useMemo(
    () => (allLoaded ? mergeWorksAndEvents(works, events) : []),
    [works, events, allLoaded]
  );

  // 利用可能なジャンル一覧
  const availableGenres = useMemo(
    () => extractGenresFromTimelineItems(timelineItems, language),
    [timelineItems, language]
  );

  // 利用可能な年代一覧
  const availableDecades = useMemo(
    () => getAvailableDecades(timelineItems),
    [timelineItems]
  );

  // URLパラメータからフィルタ状態を復元（利用可能な年代範囲でデフォルト値を設定）
  const { selectedGenres, decadeRange } = useMemo(() => {
    const parsed = parseTimelineQueryParams(searchParams);
    const defaultDecadeRange: [number, number] =
      availableDecades.length > 0
        ? [availableDecades[0]!, availableDecades[availableDecades.length - 1]!]
        : [1960, 2020];

    return {
      selectedGenres: parsed.genres ?? [],
      decadeRange: parsed.decadeRange ?? defaultDecadeRange,
    };
  }, [searchParams, availableDecades]);

  // フィルタを更新する関数
  const updateFilters = useCallback(
    (newGenres: string[], newDecadeRange: [number, number]) => {
      const queryString = buildTimelineQueryParams({
        genres: newGenres.length > 0 ? newGenres : undefined,
        decadeRange: newDecadeRange,
      });
      setSearchParams(queryString ? `?${queryString}` : '');
    },
    [setSearchParams]
  );

  // フィルタを適用
  const filteredItems = useMemo(() => {
    return filterTimelineItems(
      timelineItems,
      {
        genres: selectedGenres,
        decadeRange,
        searchQuery: '',
      },
      language
    );
  }, [timelineItems, selectedGenres, decadeRange, language]);

  // chronologyエントリにもフィルタを適用
  const filteredChronology = useMemo(() => {
    return filterChronologyEntries(chronology, decadeRange);
  }, [chronology, decadeRange]);

  // 現在のフィルタ状態を含むURL（詳細ページから戻る用）
  const currentFilterUrl = useMemo(() => {
    const queryString = buildTimelineQueryParams({
      genres: selectedGenres.length > 0 ? selectedGenres : undefined,
      decadeRange,
    });
    return `/timeline${queryString ? `?${queryString}` : ''}`;
  }, [selectedGenres, decadeRange]);

  // ジャンル選択のトグル
  const handleGenreToggle = useCallback(
    (genre: string) => {
      const newGenres = selectedGenres.includes(genre)
        ? selectedGenres.filter((g) => g !== genre)
        : [...selectedGenres, genre];
      updateFilters(newGenres, decadeRange);
    },
    [selectedGenres, decadeRange, updateFilters]
  );

  // 年代範囲の変更
  const handleDecadeRangeChange = useCallback(
    (newDecadeRange: [number, number]) => {
      updateFilters(selectedGenres, newDecadeRange);
    },
    [selectedGenres, updateFilters]
  );

  // 年代ラベルを生成
  const formatDecadeLabel = (decade: number) => {
    return t('timeline.decadeLabel', { decade });
  };

  return (
    <div className="flex min-h-screen flex-col">
      <Header />

      <main id="main-content" className="flex-1">
        {/* ページタイトル */}
        <section className="border-b bg-muted/20 py-8">
          <div className="container mx-auto px-4">
            <h1 className="text-balance text-3xl font-bold">
              {t('timeline.title')}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {t('timeline.description')}
            </p>
          </div>
        </section>

        {/* フィルタUI */}
        <section className="border-b bg-background py-4">
          <div className="container mx-auto px-4 space-y-4">
            {/* ジャンルボタン */}
            {availableGenres.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {availableGenres.map((genre) => (
                  <button
                    key={genre}
                    onClick={() => handleGenreToggle(genre)}
                    aria-pressed={selectedGenres.includes(genre)}
                    className={cn(
                      'rounded-full px-3 py-1.5 text-sm font-medium transition-colors',
                      getGenreBackgroundClass(
                        genre,
                        selectedGenres.includes(genre)
                      )
                    )}
                  >
                    {genre}
                  </button>
                ))}
              </div>
            )}

            {/* 年代範囲選択 */}
            {availableDecades.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={decadeRange[0]}
                  onChange={(e) =>
                    handleDecadeRangeChange([
                      Number(e.target.value),
                      decadeRange[1],
                    ])
                  }
                  className="rounded-lg border-2 border-input bg-background px-4 py-2 text-base font-medium transition-colors hover:border-primary focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  data-testid="decade-start-select"
                  aria-label={t('timeline.decadeRangeStart')}
                >
                  {availableDecades
                    .filter((d) => d <= decadeRange[1])
                    .map((decade) => (
                      <option key={decade} value={decade}>
                        {formatDecadeLabel(decade)}
                      </option>
                    ))}
                </select>
                <span className="text-base font-medium text-muted-foreground">
                  {t('common.rangeSeparator')}
                </span>
                <select
                  value={decadeRange[1]}
                  onChange={(e) =>
                    handleDecadeRangeChange([
                      decadeRange[0],
                      Number(e.target.value),
                    ])
                  }
                  className="rounded-lg border-2 border-input bg-background px-4 py-2 text-base font-medium transition-colors hover:border-primary focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  data-testid="decade-end-select"
                  aria-label={t('timeline.decadeRangeEnd')}
                >
                  {availableDecades
                    .filter((d) => d >= decadeRange[0])
                    .map((decade) => (
                      <option key={decade} value={decade}>
                        {formatDecadeLabel(decade)}
                      </option>
                    ))}
                </select>
              </div>
            )}

            {/* アイテム数表示 */}
            <p
              className="text-sm text-muted-foreground"
              data-testid="timeline-item-count"
            >
              {t('timeline.itemCount', { count: filteredItems.length })}
            </p>
          </div>
        </section>

        {/* タイムラインビュー */}
        <section className="py-8">
          <div className="container mx-auto px-4">
            <TimelineView
              items={filteredItems}
              chronologyEntries={filteredChronology}
              currentFilterUrl={currentFilterUrl}
              isLoading={isLoading}
              error={error || undefined}
            />
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
